import React from 'react';

const conditions: [RenderingCondition['type'], string][] = [
    ['runtimeVariable', 'Runtime variable equals'],
    ['hasPrinter', 'Printer available'],
    ['hasEmailSharingEnabled', 'Email sharing enabled'],
    ['hasTextSharingEnabled', 'Text sharing enabled'],
    ['hasSelectedMedia', 'Media selected'],
    ['hasCompletedCapture', 'Capture completed'],
    ['valueComparison', 'Value comparison'],
];

export default function ConditionEditor({ label, condition, onChange }: {
    label: string;
    condition?: RenderingCondition;
    onChange: (condition?: RenderingCondition) => void;
}) {
    const inputClass = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm';
    const metadata = condition?.metadata || {};
    const update = (key: string, value: string) => {
        if (condition) onChange({ ...condition, metadata: { ...metadata, [key]: value } });
    };
    return (
        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="block text-sm font-medium text-gray-900">
                {label}
                <select aria-label={label} className={`${inputClass} mt-2`} value={condition?.type || ''} onChange={(event) => {
                    const type = event.target.value as RenderingCondition['type'];
                    onChange(type ? { type, metadata: type === 'runtimeVariable' ? { key: '', value: 'true' } : {} } : undefined);
                }}>
                    <option value="">Always</option>
                    {conditions.map(([type, name]) => <option key={type} value={type}>{name}</option>)}
                </select>
            </label>
            {condition?.type === 'runtimeVariable' && <>
                <label className="block text-xs">Variable name
                    <input aria-label={`${label} variable name`} className={inputClass} value={metadata.key || ''} onChange={(event) => update('key', event.target.value)} />
                </label>
                <label className="block text-xs">Expected value
                    <input aria-label={`${label} expected value`} className={inputClass} value={metadata.value ?? 'true'} onChange={(event) => update('value', event.target.value)} />
                </label>
                <p className="text-xs text-gray-600">Use the exact runtime variable name and value, such as printing_enabled and true. A missing variable does not match.</p>
            </>}
            {condition?.type === 'valueComparison' && <>
                <input aria-label={`${label} left operand`} className={inputClass} placeholder="Left variable or token" value={metadata.lhs || ''} onChange={(event) => update('lhs', event.target.value)} />
                <select aria-label={`${label} operator`} className={inputClass} value={metadata.operator || 'equal'} onChange={(event) => update('operator', event.target.value)}>
                    {['equal', 'notEqual', 'lengthEqual', 'lengthNotEqual', 'lengthGreaterThan', 'lengthLessThan', 'lengthGreaterThanOrEqual', 'lengthLessThanOrEqual'].map((operator) => <option key={operator}>{operator}</option>)}
                </select>
                <input aria-label={`${label} right operand`} className={inputClass} placeholder="Right variable, token, or length" value={metadata.rhs || ''} onChange={(event) => update('rhs', event.target.value)} />
            </>}
            {condition && <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" aria-label={`Invert ${label}`} checked={condition.not || false} onChange={(event) => onChange({ ...condition, not: event.target.checked })} />
                Invert condition
            </label>}
        </div>
    );
}
